import { Routes, Route, Navigate } from 'react-router-dom';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { GuestRoute } from '@/components/auth/GuestRoute';
import { LandingRedirect } from '@/components/auth/LandingRedirect';
import { AppLayout } from '@/components/layout/AppLayout';

import { LoginPage } from '@/pages/auth/LoginPage';
import { RegisterPage } from '@/pages/auth/RegisterPage';
import { ForgotPasswordPage } from '@/pages/auth/ForgotPasswordPage';
import { OnboardingPage } from '@/pages/auth/OnboardingPage';
import { PendingApprovalPage } from '@/pages/auth/PendingApprovalPage';
import { CompleteInvitePage } from '@/pages/auth/CompleteInvitePage';

import { DashboardPage } from '@/pages/app/DashboardPage';
import { ProcessesPage } from '@/pages/app/ProcessesPage';
import { NewProcessPage } from '@/pages/app/NewProcessPage';
import { ProcessDetailPage } from '@/pages/app/ProcessDetailPage';
import { ProcessBatteryPage } from '@/pages/app/ProcessBatteryPage';
import { CandidatesPage } from '@/pages/app/CandidatesPage';
import { InviteCandidatePage } from '@/pages/app/InviteCandidatePage';
import { ResultsPage } from '@/pages/app/ResultsPage';
import { ResultDetailPage } from '@/pages/app/ResultDetailPage';
import { ResultPdfPage } from '@/pages/app/ResultPdfPage';
import { SettingsPage } from '@/pages/app/SettingsPage';
import { SettingsPoliciesPage } from '@/pages/app/SettingsPoliciesPage';
import { TeamPage } from '@/pages/app/TeamPage';
import { Nom035Page } from '@/pages/app/Nom035Page';
import { Nom035ResultsPage } from '@/pages/app/Nom035ResultsPage';
import { ClimateResultsPage } from '@/pages/app/ClimateResultsPage';
import { CultureDashboard } from '@/pages/app/CultureDashboard';
import { BitacoraHistoryPage } from '@/pages/app/BitacoraHistoryPage';
import { ComplaintsAdminPage } from '@/pages/app/ComplaintsAdminPage';

import { Nom35AssessmentPage } from '@/pages/public/Nom35AssessmentPage';
import { ClimateAssessmentPage } from '@/pages/public/ClimateAssessmentPage';
import { CultureAssessmentPage } from '@/pages/public/CultureAssessmentPage';
import { ComplaintSubmissionPage } from '@/pages/public/ComplaintSubmissionPage';

import { CandidateEntryPage } from '@/pages/candidate/CandidateEntryPage';
import { ConsentPage } from '@/pages/candidate/ConsentPage';
import { InstructionsPage } from '@/pages/candidate/InstructionsPage';
import { TestPage } from '@/pages/candidate/TestPage';
import { FinishPage } from '@/pages/candidate/FinishPage';
import { ThanksPage } from '@/pages/candidate/ThanksPage';
import { ErrorInvalidPage } from '@/pages/candidate/ErrorInvalidPage';
import { ErrorCompletedPage } from '@/pages/candidate/ErrorCompletedPage';

import { OwnerLayout } from '@/pages/owner/OwnerLayout';
import { OwnerDashboardPage } from '@/pages/owner/OwnerDashboardPage';
import { OwnerCompaniesPage } from '@/pages/owner/OwnerCompaniesPage';
import { OwnerCompanyCreatePage } from '@/pages/owner/OwnerCompanyCreatePage';
import { OwnerCompanyDetailPage } from '@/pages/owner/OwnerCompanyDetailPage';
import { OwnerUsersPage } from '@/pages/owner/OwnerUsersPage';
import { OwnerUserDetailPage } from '@/pages/owner/OwnerUserDetailPage';
import { OwnerBillingPage } from '@/pages/owner/OwnerBillingPage';
import { OwnerAuditPage } from '@/pages/owner/OwnerAuditPage';
import { OwnerSettingsPage } from '@/pages/owner/OwnerSettingsPage';

import { BillingPage } from '@/pages/BillingPage';
import { OfflinePage } from '@/pages/OfflinePage';
import { MaintenancePage } from '@/pages/MaintenancePage';

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<LandingRedirect />} />

      {/* Auth — solo invitados */}
      <Route path="/login" element={<GuestRoute><LoginPage /></GuestRoute>} />
      <Route path="/register" element={<GuestRoute><RegisterPage /></GuestRoute>} />
      <Route path="/forgot-password" element={<GuestRoute><ForgotPasswordPage /></GuestRoute>} />
      <Route path="/auth/complete-invite" element={<GuestRoute><CompleteInvitePage /></GuestRoute>} />
      <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
      <Route path="/pending-approval" element={<ProtectedRoute><PendingApprovalPage /></ProtectedRoute>} />

      {/* Panel RH */}
      <Route path="/app" element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
        <Route index element={<Navigate to="/app/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="processes" element={<ProcessesPage />} />
        <Route path="processes/new" element={<NewProcessPage />} />
        <Route path="processes/:id" element={<ProcessDetailPage />} />
        <Route path="processes/:id/battery" element={<ProcessBatteryPage />} />
        <Route path="processes/:id/candidates" element={<CandidatesPage />} />
        <Route path="processes/:id/candidates/invite" element={<InviteCandidatePage />} />
        <Route path="results" element={<ResultsPage />} />
        <Route path="results/:attemptId" element={<ResultDetailPage />} />
        <Route path="results/:attemptId/pdf" element={<ResultPdfPage />} />
        <Route path="nom035" element={<Nom035Page />} />
        <Route path="culture" element={<CultureDashboard />} />
        <Route path="nom035/results/:campaignId" element={<Nom035ResultsPage />} />
        <Route path="bitacora/:employeeId" element={<BitacoraHistoryPage />} />
        <Route path="complaints" element={<ComplaintsAdminPage />} />
        <Route path="climate/results" element={<ClimateResultsPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="settings/policies" element={<SettingsPoliciesPage />} />
        <Route path="billing" element={<BillingPage />} />
        <Route path="team" element={<ProtectedRoute requireAdmin><TeamPage /></ProtectedRoute>} />
      </Route>

      {/* Evaluaciones públicas (sin Auth) */}
      <Route path="/complaint/:companyId" element={<ComplaintSubmissionPage />} />
      <Route path="/assessment/:id" element={<Nom35AssessmentPage />} />
      <Route path="/climate" element={<ClimateAssessmentPage />} />
      <Route path="/climate/:id" element={<ClimateAssessmentPage />} />
      <Route path="/culture/:token" element={<CultureAssessmentPage />} />

      {/* Candidato (sin Auth, por token) */}
      <Route path="/c" element={<Navigate to="/c/error/invalid" replace />} />
      <Route path="/c/error/invalid" element={<ErrorInvalidPage />} />
      <Route path="/c/error/completed" element={<ErrorCompletedPage />} />
      <Route path="/c/:token" element={<CandidateEntryPage />} />
      <Route path="/c/:token/consent" element={<ConsentPage />} />
      <Route path="/c/:token/instructions" element={<InstructionsPage />} />
      <Route path="/c/:token/test" element={<TestPage />} />
      <Route path="/c/:token/finish" element={<FinishPage />} />
      <Route path="/c/:token/thanks" element={<ThanksPage />} />

      {/* Owner */}
      <Route path="/owner" element={<ProtectedRoute requireOwner><OwnerLayout /></ProtectedRoute>}>
        <Route index element={<Navigate to="/owner/dashboard" replace />} />
        <Route path="dashboard" element={<OwnerDashboardPage />} />
        <Route path="companies" element={<OwnerCompaniesPage />} />
        <Route path="companies/new" element={<OwnerCompanyCreatePage />} />
        <Route path="companies/:companyId" element={<OwnerCompanyDetailPage />} />
        <Route path="users" element={<OwnerUsersPage />} />
        <Route path="users/:uid" element={<OwnerUserDetailPage />} />
        <Route path="billing" element={<OwnerBillingPage />} />
        <Route path="audit" element={<OwnerAuditPage />} />
        <Route path="settings" element={<OwnerSettingsPage />} />
      </Route>

      {/* Billing: redirect legacy /billing to app */}
      <Route path="/billing" element={<Navigate to="/app/billing" replace />} />
      <Route path="/offline" element={<OfflinePage />} />
      <Route path="/maintenance" element={<MaintenancePage />} />

      <Route path="*" element={<Navigate to="/app/dashboard" replace />} />
    </Routes>
  );
}
